// Frontend log sink. Captures console.warn/error, window.onerror,
// unhandledrejection, and explicit `slog.*` calls. POSTs batches to
// /v1/logs (sendBeacon when available) so the backend can expose them
// for remote debugging via /v1/logs/recent.

type Level = 'info' | 'warn' | 'error';

interface Entry {
  t: number;
  lvl: Level;
  msg: string;
  data?: unknown;
  url?: string;
}

const SESSION =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);

const buffer: Entry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;
const FLUSH_MS = 800;
const MAX_BATCH = 50;

function safeSerialize(v: unknown): unknown {
  if (v instanceof Error) {
    return { name: v.name, message: v.message, stack: v.stack };
  }
  if (typeof v === 'object' && v !== null) {
    try {
      return JSON.parse(JSON.stringify(v));
    } catch {
      return String(v);
    }
  }
  return v;
}

function fmt(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(' ');
}

function enqueue(lvl: Level, msg: string, data?: unknown) {
  buffer.push({
    t: Date.now(),
    lvl,
    msg,
    data: data === undefined ? undefined : safeSerialize(data),
    url: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
  if (buffer.length >= MAX_BATCH) {
    void flush();
    return;
  }
  if (!flushTimer) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void flush();
    }, FLUSH_MS);
  }
}

async function flush() {
  if (buffer.length === 0) return;
  const batch = buffer.splice(0, buffer.length);
  const payload = JSON.stringify({
    session: SESSION,
    ua: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    entries: batch,
  });
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      const ok = navigator.sendBeacon('/v1/logs', blob);
      if (ok) return;
    }
    await fetch('/v1/logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    });
  } catch {
    // Drop silently; we don't want logSink failures to themselves spam.
  }
}

export const slog = {
  info(msg: string, data?: unknown) {
    enqueue('info', msg, data);
  },
  warn(msg: string, data?: unknown) {
    enqueue('warn', msg, data);
  },
  error(msg: string, data?: unknown) {
    enqueue('error', msg, data);
  },
  flush,
  session: () => SESSION,
};

export function initLogSink() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  // Capture unhandled errors + rejections at the earliest possible point.
  window.addEventListener('error', (e) => {
    enqueue('error', 'window.error', {
      message: e.message,
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
      stack: (e.error as Error | undefined)?.stack,
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    enqueue('error', 'unhandledrejection', { reason: safeSerialize(e.reason) });
  });

  // Tee console.warn/error so library warnings surface remotely.
  const origWarn = console.warn.bind(console);
  console.warn = (...args: unknown[]) => {
    origWarn(...(args as Parameters<typeof console.warn>));
    enqueue('warn', fmt(args));
  };
  const origError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    origError(...(args as Parameters<typeof console.error>));
    enqueue('error', fmt(args));
  };

  // Initial environment snapshot — hugely helpful for remote debugging.
  enqueue('info', 'boot', {
    href: window.location.href,
    viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio },
    online: navigator.onLine,
    languages: navigator.languages,
    platform: (navigator as Navigator & { platform?: string }).platform ?? null,
    standalone: (navigator as Navigator & { standalone?: boolean }).standalone ?? null,
    secureContext: window.isSecureContext,
    mediaDevices: !!navigator.mediaDevices,
    getUserMedia: !!navigator.mediaDevices?.getUserMedia,
    permissions: !!navigator.permissions,
    audioContext: typeof (window.AudioContext || (window as unknown as { webkitAudioContext?: unknown }).webkitAudioContext) !== 'undefined',
    webgl: (() => {
      try {
        const c = document.createElement('canvas');
        return !!(c.getContext('webgl') || c.getContext('experimental-webgl'));
      } catch {
        return false;
      }
    })(),
    appVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
  });

  // Flush on tab hide / unload to catch the last events.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
  window.addEventListener('pagehide', () => void flush());
}
